/**
 * Calculates the week number of a given date.
 * @param { Date } inputDate - The date for which to calculate the week number.
 * @returns { number } - The week number.
 */
export const getWeek = (inputDate) => {
    const date = new Date(inputDate.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

/**
 * Formats a date to "Day, DD.MM.YYYY" format in German locale.
 * @param { Date|string } date - The date to format.
 * @returns { string } - Formatted date string in "Day, DD.MM.YYYY" format.
 */
export const formatDate = (date) => {
    const formatDate = new Date(date);
    const weekDays = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
    const weekDay = weekDays[formatDate.getDay()];

    // Format date to "DD.MM.YYYY" format in German locale
    const formattedDate = formatDate.toLocaleDateString("de-DE", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    return `${weekDay}, der ${formattedDate}`;
};

/**
 * Formats a date to "DD.MM" format with abbreviated weekday in German.
 * @param { Date|string } date - The date to format.
 * @returns { string } - Formatted date string in "Abbr.Weekday DD.MM" format.
 */
export const formatDate2 = (date) => {
    const formatDate = new Date(date);
    const weekDaysShort = ["So.", "Mo.", "Di.", "Mi.", "Do.", "Fr.", "Sa."];
    const weekDay = weekDaysShort[formatDate.getDay()];

    // Format date to "DD.MM" format in German locale
    return `${weekDay} ${formatDate.toLocaleDateString("de-DE", {
        month: '2-digit',
        day: '2-digit'
    })}`;
};

/**
 * Formats a date to ISO format "YYYY-MM-DD".
 * @param  { Date|string } date - The date to format.
 * @returns { string } - Formatted date string in "YYYY-MM-DD" format.
 */
export const formatDate3 = (date) => {
    let yourDate = new Date(date);
    let offset = yourDate.getTimezoneOffset();

    // Adjust date to UTC to avoid timezone differences
    yourDate = new Date(yourDate.getTime() - (offset * 60 * 1000));

    // Format date to ISO "YYYY-MM-DD" format
    return yourDate.toISOString().split('T')[0];
};

/**
 * Formats a day number (1-7) to German weekday.
 * @param { number } day - Day number (1 for Monday, 7 for Sunday).
 * @returns { string } - German weekday name or empty string if out of range.
 */
export const formatWeekday = (day) => {
    const weekDays = ["", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
    return weekDays[day] || "";
};

/**
 * Formats a week range for a given ISO week and year.
 * @param { number } week - The ISO week number.
 * @param { number } year - The year.
 * @returns { string } - The formatted week range in "DD.MM - DD.MM" format.
 */
export const formatCalendarWeekString = (week, year) => {
    return `${getISOWeekStart(week, year, 0).toLocaleDateString("de-DE", { month: '2-digit', day: '2-digit' })} - ${getISOWeekStart(week, year, 6).toLocaleDateString("de-DE", { month: '2-digit', day: '2-digit' })}`;
};

/**
 * Calculates the starting date of an ISO week.
 * @param { number } week - The ISO week number.
 * @param { number } year - The year.
 * @param { number } day - Offset to get a specific day of the week (0 = Monday, 6 = Sunday).
 * @returns { Date } - The calculated date.
 */
export const getISOWeekStart = (week, year, day) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7); // Approximate start of the week
    const dow = simple.getDay();
    const ISOweekStart = simple;

    if (dow <= 4) {
        ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1 + day);
    } else {
        ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay() + day);
    }

    return ISOweekStart;
};

/**
 * Formats a time string to "HH:MM" format.
 * @param { string } time - The time string in "HH:MM:SS" or "HH:MM" format.
 * @returns { string } - The formatted time string in "HH:MM" format.
 */
export const formatTime = (time) => {
    const array = time.split(":");

    // Check if array has at least hours and minutes
    if (array.length < 2) {
        throw new Error("Invalid time format. Expected format is 'HH:MM' or 'HH:MM:SS'.");
    }

    return `${array[0]}:${array[1]}`;
};/**
 * Berechnet den Start- und Enddatum einer ISO-Kalenderwoche.
 */
export function getWeekDateRange(week, year) {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();

    const ISOweekStart = new Date(simple);
    if (dow <= 4)
        ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else
        ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());

    const ISOweekEnd = new Date(ISOweekStart);
    ISOweekEnd.setDate(ISOweekStart.getDate() + 6);

    return {
        startDate: ISOweekStart.toLocaleDateString("de-DE"),
        endDate: ISOweekEnd.toLocaleDateString("de-DE")
    };
}

// ==================== SEASONAL FUNCTIONS ====================

/**
 * Checks if the current date is during Christmas season (Dec 20-31).
 * @returns {boolean} True if currently in Christmas season.
 */
export const isChristmasSeason = () => {
	const today = new Date();
	const month = today.getMonth();
	const day = today.getDate();
	return month === 11 && day >= 20; // December 20-31
};

